import React from "react";
import { CheckCircle2, Circle, Clock, AlertTriangle } from "lucide-react";

// Canonical 7-stage Mail-In pipeline from the TreasureCare Master Operations
// Map. Aliases (submitted/validated/etc) map to these codes so old claims
// still render correctly on the timeline.
const STAGES = [
  { key: "booking_confirmed",    label: "Booking Confirmed",     desc: "We've received your booking" },
  { key: "packaging_dispatched", label: "Packaging Dispatched",  desc: "Prepaid packaging on its way" },
  { key: "device_collected",     label: "Device Collected",      desc: "Picked up, heading to workshop" },
  { key: "in_workshop",          label: "In Workshop",           desc: "Engineers are working on it" },
  { key: "repair_complete",      label: "Repair Complete",       desc: "Repair finished — QA next" },
  { key: "quality_checked",      label: "Quality Checked",       desc: "Passed our quality checks" },
  { key: "returned",             label: "Returned",              desc: "On its way back to you" },
];

// Backwards-compat: map legacy status codes to the new 7-stage codes.
const LEGACY_TO_STAGE = {
  submitted: "booking_confirmed",
  validated: "packaging_dispatched",
  in_transit: "device_collected",
  received: "in_workshop",
  diagnosed: "in_workshop",
  repairing: "in_workshop",
  repaired: "repair_complete",
  closed: "quality_checked",
  returned: "returned",
};

export default function StatusTimeline({ status, stage, events = [], showProgress = true, stuck = false }) {
  // Prefer explicit `stage` if provided by the API; else translate legacy status.
  const currentKey = stage || LEGACY_TO_STAGE[status] || status || "booking_confirmed";
  const idx = STAGES.findIndex((s) => s.key === currentKey);
  const currentIdx = idx < 0 ? 0 : idx;
  const percent = Math.round(((currentIdx + 1) / STAGES.length) * 100);
  const nextStep = STAGES[currentIdx + 1];

  return (
    <div data-testid="status-timeline">
      {showProgress && (
        <div className="mb-6" data-testid="claim-progress-bar">
          <div className="flex items-baseline justify-between mb-2">
            <div className="overline text-secondary text-xs">Overall progress</div>
            <div className="text-xs text-muted-foreground">
              {percent}% · {STAGES[currentIdx]?.label}
            </div>
          </div>
          <div className="w-full h-2 rounded-full bg-primary/10 overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${percent}%` }}
            />
          </div>
          {stuck && (
            <div className="mt-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-destructive/15 text-destructive" data-testid="stage-stuck-flag">
              <AlertTriangle className="w-3 h-3" /> This job has been in the current stage for over 48h — your account manager is being alerted.
            </div>
          )}
          {nextStep && (
            <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Next milestone: <span className="text-foreground">{nextStep.label}</span>
            </div>
          )}
        </div>
      )}
      <div className="relative pl-6">
        <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-primary/15" />
        {STAGES.map((step, i) => {
          const done = i < currentIdx;
          const current = i === currentIdx;
          const upcoming = i > currentIdx;
          // Timeline events may store either the new stage code OR the legacy status
          const event = events.find((e) => e.status === step.key)
              || events.find((e) => LEGACY_TO_STAGE[e.status] === step.key);
          return (
            <div key={step.key} className="relative pb-6 last:pb-0" data-testid={`stage-${step.key}`}>
              <div className="absolute -left-6 top-0 w-4 h-4 rounded-full flex items-center justify-center">
                {done ? (
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                ) : current ? (
                  <span className="w-3.5 h-3.5 rounded-full bg-secondary ring-4 ring-secondary/25 animate-pulse-dot" />
                ) : (
                  <Circle className="w-4 h-4 text-muted-foreground/40" />
                )}
              </div>
              <div className={`ml-2 ${upcoming ? "opacity-40" : ""}`}>
                <div className="flex items-center gap-2">
                  <div className={`font-medium ${current ? "text-primary" : ""}`}>{step.label}</div>
                  {current && <span className="text-[10px] overline text-secondary">In progress</span>}
                </div>
                <div className="text-xs text-muted-foreground">{step.desc}</div>
                {event?.at && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3" /> {new Date(event.at).toLocaleString()}
                  </div>
                )}
                {event?.note && <div className="text-xs text-foreground/70 mt-1">{event.note}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
